import axios from 'axios';
import { TOKEN, buildPublicApiUrl } from '@/utils/config';

const DEFAULT_CHANNEL = 1000;
const DEFAULT_TIMEOUT = 10000;
const TOKEN_STORAGE_KEYS = ['tcm_study_token', 'baby_album_token', 'token'];
const MOCK_STORAGE_KEY = 'tcm_study_mock_enabled';

function getLocalStorage () {
    try {
        return window.localStorage || null;
    } catch (error) {
        return null;
    }
}

function normalizeToken (token) {
    return String(token || '').replace(/^Bearer\s+/i, '').trim();
}

function resolveChannelFromToken (token) {
    const normalizedToken = normalizeToken(token);
    if (!normalizedToken) {
        return '';
    }

    const tokenParts = normalizedToken.split('.');
    if (tokenParts.length < 2) {
        return '';
    }

    try {
        const base64Payload = tokenParts[1].replace(/-/g, '+').replace(/_/g, '/');
        const padding = new Array((4 - (base64Payload.length % 4)) % 4 + 1).join('=');
        const payload = JSON.parse(atob(`${base64Payload}${padding}`));
        return String((payload.data && payload.data.channel) || '');
    } catch (error) {
        return '';
    }
}

export function resolveTcmStudyToken (route) {
    const query = route && route.query ? route.query : {};
    const storage = getLocalStorage();
    if (storage && Object.prototype.hasOwnProperty.call(query, 'mock')) {
        storage.setItem(MOCK_STORAGE_KEY, query.mock === '1' || query.mock === 'true' ? '1' : '0');
    }

    const queryToken = normalizeToken(query.token || query.Authorization || query.authorization);
    if (queryToken) {
        storage && storage.setItem('tcm_study_token', queryToken);
        return queryToken;
    }

    if (query.type === 'test') {
        return TOKEN;
    }

    if (storage) {
        for (let i = 0; i < TOKEN_STORAGE_KEYS.length; i += 1) {
            const token = normalizeToken(storage.getItem(TOKEN_STORAGE_KEYS[i]));
            if (token) {
                return token;
            }
        }
    }

    return '';
}

function requestHeaders (token) {
    const normalizedToken = normalizeToken(token);
    const headers = {
        Channel: resolveChannelFromToken(normalizedToken) || String(DEFAULT_CHANNEL)
    };

    if (normalizedToken) {
        headers.Authorization = `Bearer ${normalizedToken}`;
    }

    return headers;
}

function unwrapResponse (response, fallbackMessage) {
    const data = response && response.data ? response.data : {};
    if (data.success) {
        return data.result;
    }

    const errors = data.errors || {};
    const message = errors.message || data.message || fallbackMessage || '请求失败';
    const error = new Error(message);
    error.code = errors.code || errors.error_code || '';
    error.responseData = data;
    throw error;
}

function isMockEnabled () {
    if (process.env.NODE_ENV === 'production') {
        return false;
    }
    const storage = getLocalStorage();
    return process.env.VUE_APP_TCM_STUDY_MOCK === 'true'
        || (storage && storage.getItem(MOCK_STORAGE_KEY) === '1');
}

function mockResult (method, path, options = {}) {
    const today = new Date().toISOString().slice(0, 10);
    const month = today.slice(0, 7);
    const recordDate = (options.params && options.params.record_date) || (options.data && options.data.record_date) || today;

    if (path === '/tcmstudy/home') {
        return {
            is_enrolled: true,
            has_study_access: true,
            can_submit: true,
            cannot_submit_reason: '',
            subject: {
                id: 1,
                study_no: 'TCM-MOCK-001',
                baby_id: 1,
                group_code: 'A',
                intervention_type: 'massage_plan_1',
                study_status: 'active',
                training_status: 'passed',
                consent_status: 'signed'
            },
            babies: [
                {
                    id: 1,
                    baby_id: 1,
                    baby_name: '宝宝A',
                    user_number: 'TCM-MOCK-001',
                    is_enrolled: true,
                    subject_id: 1,
                    study_no: 'TCM-MOCK-001'
                },
                {
                    id: 2,
                    baby_id: 2,
                    baby_name: '宝宝B',
                    user_number: 'TCM-MOCK-002',
                    is_enrolled: false,
                    subject_id: null,
                    study_no: ''
                }
            ],
            current_baby_id: 1,
            baby_profile: {
                baby_id: 1,
                baby_name: '宝宝A',
                user_number: 'TCM-MOCK-001',
                corrected_age_month: 5.8,
                corrected_age_text: '5月24天',
                age_stage: 'within_6_months',
                age_stage_text: '6月龄及以内'
            },
            task_record: {
                massage_status: 'not_started'
            },
            massage_entry: {
                entry_status: 'available',
                lock_reason: '',
                research_group_status: 'joined',
                training_status: 'passed',
                task_status: 'not_started',
                completed_count: 0,
                tutorial_url: 'https://yboss.yubinglabs.com/discuz/news/2026-04-21/c5aHLWJ6Pnj.html'
            },
            need_previous_massage_record: false,
            previous_massage_record: null,
            daily_tasks: [
                { key: 'morning_milk', time_label: '早起', type: 'milk', title: '奶', status: 'not_started', status_text: '待记录', action_text: '编辑' },
                { key: 'massage', time_label: '1小时后', type: 'massage', title: '小儿推拿', summary: '温阳、健脾、保健', status: 'not_started', status_text: '待查看', entry_status: 'available', locked: false, tutorial_url: 'https://yboss.yubinglabs.com/discuz/news/2026-04-21/c5aHLWJ6Pnj.html' },
                { key: 'forenoon_milk', time_label: '2-3h后', type: 'milk', title: '奶', status: 'not_started', status_text: '待记录', action_text: '编辑' },
                { key: 'noon_milk', time_label: '中午', type: 'milk', title: '奶', status: 'not_started', status_text: '待记录', action_text: '编辑' },
                { key: 'afternoon_milk', time_label: '2-3h后', type: 'milk', title: '奶', status: 'not_started', status_text: '待记录', action_text: '编辑' },
                { key: 'evening_milk', time_label: '傍晚', type: 'milk', title: '奶', status: 'not_started', status_text: '待记录', action_text: '编辑' },
                { key: 'evening_massage', time_label: '1小时后', type: 'massage', title: '小儿推拿', summary: '温阳、健脾、保健', status: 'not_started', status_text: '待查看', entry_status: 'available', locked: false, tutorial_url: 'https://yboss.yubinglabs.com/discuz/news/2026-04-21/c5aHLWJ6Pnj.html' },
                { key: 'bedtime_milk', time_label: '睡前', type: 'milk', title: '奶', status: 'not_started', status_text: '待记录', action_text: '编辑' }
            ],
            today: {
                record_date: today,
                operation_status: 'missing',
                feeding_status: 'completed',
                feeding_extend_status: 'missing'
            }
        };
    }

    if (path === '/tcmstudy/daily_operation' && method === 'get') {
        return {};
    }

    if (path === '/tcmstudy/daily_operation/history') {
        return [
            {
                id: 101,
                record_date: today,
                is_completed: 1,
                remark: 'mock 操作记录'
            }
        ];
    }

    if (path === '/tcmstudy/daily_feeding_status') {
        return {
            record_date: recordDate,
            has_feeding_record: true,
            feeding_status: 'completed',
            feeding_extend_status: 'missing',
            feeding_summary: {
                feeding_count: 8,
                total_milk_volume_ml: 560,
                average_milk_volume_ml: 70,
                total_feeding_duration_min: 144,
                average_feeding_duration_min: 18
            },
            feeding_extend: null
        };
    }

    if (path === '/tcmstudy/daily_feeding_extend' && method === 'get') {
        return {};
    }

    if (path === '/tcmstudy/month_summary') {
        return {
            stat_month: (options.params && options.params.stat_month) || month,
            planned_operation_count: 30,
            actual_operation_count: 18,
            makeup_count: 2,
            missing_count: 12,
            abnormal_count: 1,
            compliance_rate: 60
        };
    }

    return { id: Date.now() };
}

function normalizeRequestError (error, fallbackMessage) {
    if (error && error.responseData) {
        return error;
    }

    if (error && error.response) {
        const status = error.response.status;
        const message = status === 404
            ? '接口不存在'
            : `${fallbackMessage || '请求失败'}，HTTP ${status}`;
        const normalizedError = new Error(message);
        normalizedError.code = `HTTP_${status}`;
        normalizedError.responseData = error.response.data || {};
        return normalizedError;
    }

    if (error && error.code === 'ECONNABORTED') {
        const timeoutError = new Error('接口请求超时，请稍后重试');
        timeoutError.code = 'REQUEST_TIMEOUT';
        return timeoutError;
    }

    const networkError = new Error((error && error.message) || fallbackMessage || '网络请求失败');
    networkError.code = (error && error.code) || 'NETWORK_ERROR';
    return networkError;
}

function request (token, method, path, options = {}) {
    if (isMockEnabled()) {
        return Promise.resolve(mockResult(method, path, options));
    }

    return axios({
        method,
        url: buildPublicApiUrl(path),
        params: options.params || {},
        data: options.data || {},
        headers: requestHeaders(token),
        timeout: options.timeout || DEFAULT_TIMEOUT
    })
        .then(response => unwrapResponse(response, options.fallbackMessage))
        .catch(error => {
            throw normalizeRequestError(error, options.fallbackMessage);
        });
}

export function fetchTcmStudyHome (token, babyId, date) {
    return request(token, 'get', '/tcmstudy/home', {
        params: {
            baby_id: babyId || undefined,
            date: date || undefined
        },
        fallbackMessage: '获取研究首页失败'
    });
}

export function submitMassageCheck (token, payload) {
    return request(token, 'post', '/tcmstudy/massage_check', {
        data: payload,
        fallbackMessage: '提交推拿前检查失败'
    });
}

export function submitMassagePrepare (token, payload) {
    return request(token, 'post', '/tcmstudy/massage_prepare', {
        data: payload,
        fallbackMessage: '提交推拿准备确认失败'
    });
}

export function submitMassageUnsuitable (token, payload) {
    return request(token, 'post', '/tcmstudy/massage_unsuitable', {
        data: payload,
        fallbackMessage: '提交不适合推拿原因失败'
    });
}

export function submitTaskComplete (token, payload) {
    return request(token, 'post', '/tcmstudy/task_complete', {
        data: payload,
        fallbackMessage: '提交任务完成状态失败'
    });
}

export function startMassageTimer (token, payload) {
    return request(token, 'post', '/tcmstudy/massage_timer_start', {
        data: payload,
        fallbackMessage: '开始推拿计时失败'
    });
}

export function submitMassageComplete (token, payload) {
    return request(token, 'post', '/tcmstudy/massage_complete', {
        data: payload,
        fallbackMessage: '提交推拿完成失败'
    });
}

export function submitPreviousMassageRecord (token, payload) {
    return request(token, 'post', '/tcmstudy/massage_previous_record', {
        data: payload,
        fallbackMessage: '补录上次推拿记录失败'
    });
}

export function submitMassageFeedback (token, payload) {
    return request(token, 'post', '/tcmstudy/massage_feedback', {
        data: payload,
        fallbackMessage: '提交推拿反馈失败'
    });
}

export function fetchDailyOperation (token, recordDate, subjectId) {
    return request(token, 'get', '/tcmstudy/daily_operation', {
        params: {
            record_date: recordDate,
            subject_id: subjectId || undefined
        },
        fallbackMessage: '获取操作记录失败'
    });
}

export function saveDailyOperation (token, payload, recordId) {
    const method = recordId ? 'patch' : 'post';
    const path = recordId ? `/tcmstudy/daily_operation/${recordId}` : '/tcmstudy/daily_operation';
    return request(token, method, path, {
        data: payload,
        fallbackMessage: '保存操作记录失败'
    });
}

export function fetchDailyFeedingStatus (token, recordDate, subjectId) {
    return request(token, 'get', '/tcmstudy/daily_feeding_status', {
        params: {
            record_date: recordDate,
            subject_id: subjectId || undefined
        },
        fallbackMessage: '获取喂养状态失败'
    });
}

export function fetchDailyFeedingExtend (token, recordDate, subjectId) {
    return request(token, 'get', '/tcmstudy/daily_feeding_extend', {
        params: {
            record_date: recordDate,
            subject_id: subjectId || undefined
        },
        fallbackMessage: '获取喂养补充失败'
    });
}

export function saveDailyFeedingExtend (token, payload) {
    return request(token, 'post', '/tcmstudy/daily_feeding_extend', {
        data: payload,
        fallbackMessage: '保存喂养补充失败'
    });
}

export function fetchMonthSummary (token, statMonth, subjectId) {
    return request(token, 'get', '/tcmstudy/month_summary', {
        params: {
            stat_month: statMonth,
            subject_id: subjectId || undefined
        },
        fallbackMessage: '获取月度摘要失败'
    });
}

export function fetchOperationHistory (token, page = 1, perPage = 10, subjectId) {
    return request(token, 'get', '/tcmstudy/daily_operation/history', {
        params: {
            page,
            per_page: perPage,
            subject_id: subjectId || undefined
        },
        fallbackMessage: '获取历史记录失败'
    });
}

export function submitAbnormalReport (token, payload) {
    return request(token, 'post', '/tcmstudy/abnormal_report', {
        data: payload,
        fallbackMessage: '异常上报失败'
    });
}
